import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Copy, Layers } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money } from "@/lib/api";
import { SectionTitle, EmptyState, Pill, Field } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";

const STATUS_TONE = {
  Draft: "bg-line text-stone",
  "Active Comparison": "bg-[#EDEFC4] text-ink",
  Selected: "bg-lime text-ink",
  Archived: "bg-line text-fog",
};

export default function ScenariosPanel({ projectId, spaces = [] }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [compare, setCompare] = useState([]);
  const [form, setForm] = useState({ name: "", space_id: "", mode: "current", source_scenario_id: "", description: "" });

  const load = () =>
    api.get("/scenarios", { params: { project_id: projectId } }).then((r) => setRows(r.data)).catch(() => setRows([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/scenarios", {
        name: form.name,
        project_id: projectId,
        space_id: form.space_id || null,
        description: form.description || null,
        mode: form.mode,
        source_scenario_id: form.source_scenario_id || null,
        baseline_scenario_id: form.mode === "duplicate" ? form.source_scenario_id || null : rows[0]?.id || null,
      });
      toast.success("Scenario created");
      setCreating(false);
      setForm({ name: "", space_id: "", mode: "current", source_scenario_id: "", description: "" });
      load();
      window.location.assign(`/scenarios/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleCompare = (id) =>
    setCompare((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id].slice(-3)));

  return (
    <div className="space-y-8" data-testid="scenarios-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <SectionTitle>Scenarios</SectionTitle>
        <div className="flex flex-wrap gap-3">
          {compare.length > 1 && (
            <Link
              data-testid="compare-scenarios-btn"
              to={`/scenarios/compare?ids=${compare.join(",")}`}
              className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:border-fog"
            >
              <Layers className="h-4 w-4" strokeWidth={1.5} /> Compare {compare.length}
            </Link>
          )}
          <button
            data-testid="create-scenario-btn"
            onClick={() => setCreating((v) => !v)}
            className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark"
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Create Scenario
          </button>
        </div>
      </div>

      <p className="max-w-2xl text-xs leading-relaxed text-fog">
        Scenarios are internal. Nothing here is visible to the client, and the live project is only
        touched when you explicitly apply a scenario.
      </p>

      {creating && (
        <form onSubmit={create} className="card-surface space-y-5 p-6" data-testid="scenario-create-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Scenario Name">
              <input data-testid="scenario-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} />
            </Field>
            <Field label="Space (optional)">
              <select data-testid="scenario-space-select" value={form.space_id} onChange={(e) => setForm({ ...form, space_id: e.target.value })} className={input}>
                <option value="">Whole project</option>
                {spaces.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Start From">
              <select data-testid="scenario-mode-select" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className={input}>
                <option value="current">Current selections</option>
                <option value="blank">Blank</option>
                <option value="duplicate">Duplicate existing scenario</option>
              </select>
            </Field>
            {form.mode === "duplicate" && (
              <Field label="Scenario to duplicate">
                <select data-testid="scenario-source-select" value={form.source_scenario_id} onChange={(e) => setForm({ ...form, source_scenario_id: e.target.value })} className={input}>
                  <option value="">Select…</option>
                  {rows.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Internal description" className="sm:col-span-2">
              <textarea data-testid="scenario-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
            </Field>
          </div>
          <div className="flex gap-3">
            <button data-testid="scenario-submit-btn" type="submit" className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Create</button>
            <button type="button" onClick={() => setCreating(false)} className="rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
          </div>
        </form>
      )}

      {rows.length === 0 ? (
        <EmptyState testId="scenarios-empty" title="No scenarios yet" body="Build a variation of this room to test its financial impact before you present anything." />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="scenarios-list">
          {rows.map((s) => (
            <div key={s.id} data-testid={`scenario-card-${s.id}`} className="card-surface card-hover p-6">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/scenarios/${s.id}`} className="font-serif text-xl leading-snug text-ink hover:text-lime-dark">
                  {s.name}
                </Link>
                <Pill tone={STATUS_TONE[s.status] || "bg-line text-stone"}>{s.status}</Pill>
              </div>
              {s.description && <p className="mt-3 text-sm leading-relaxed text-stone">{s.description}</p>}
              <div className="mt-6 flex items-end justify-between border-t border-line pt-5">
                <div>
                  <div className="overline">Scenario Total</div>
                  <div className="num mt-1 text-2xl text-ink">{money(s.total)}</div>
                </div>
                <span className="num text-xs text-fog">{s.item_count} items</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  data-testid={`scenario-compare-toggle-${s.id}`}
                  onClick={() => toggleCompare(s.id)}
                  className={`rounded-sm border px-3 py-1.5 text-xs transition-colors ${
                    compare.includes(s.id) ? "border-lime bg-lime text-ink" : "border-line text-stone hover:border-fog"
                  }`}
                >
                  {compare.includes(s.id) ? "Selected to compare" : "Add to comparison"}
                </button>
                <button
                  data-testid={`scenario-duplicate-${s.id}`}
                  onClick={async () => {
                    try {
                      await api.post("/scenarios", {
                        name: `${s.name} (copy)`, project_id: projectId, space_id: s.space_id || null,
                        mode: "duplicate", source_scenario_id: s.id, baseline_scenario_id: s.id,
                      });
                      toast.success("Scenario duplicated");
                      load();
                    } catch (err) {
                      toast.error(apiError(err));
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-sm border border-line px-3 py-1.5 text-xs text-stone hover:border-fog"
                >
                  <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> Duplicate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
